import React, { useState, useEffect, useCallback, useRef } from 'react';
import { serviceAPI } from '../../services/api';
import Loader from '../common/Loader';

const ServiceSelector = ({ onServiceSelect, selectedService = null, isDarkMode = false }) => {
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [serviceLevels, setServiceLevels] = useState([]);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [additionalServices, setAdditionalServices] = useState({ bags: 1 });
  const [loading, setLoading] = useState(true);
  const [loadingLevels, setLoadingLevels] = useState(false);
  const [error, setError] = useState(null);
  
  // Cache for pricing calculations to avoid repeated API calls
  const pricingCache = useRef(new Map());
  const categoriesLoaded = useRef(false);
  const onServiceSelectRef = useRef(onServiceSelect);
  
  // Update ref when onServiceSelect changes
  useEffect(() => {
    onServiceSelectRef.current = onServiceSelect;
  }, [onServiceSelect]);

  const loadCategories = useCallback(async () => {
    // Only load categories once
    if (categoriesLoaded.current) {
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Try to get dynamic pricing first, fallback to static
      let response;
      try {
        response = await serviceAPI.getAllCategoriesWithDynamicPricing();
      } catch (error) {
        console.log('Dynamic pricing unavailable, using static categories');
        response = await serviceAPI.getAllCategories();
      }
      
      setCategories(response.data);
      categoriesLoaded.current = true;
      
      // Set default selection if provided
      if (selectedService?.categoryId) {
        const category = response.data.find(cat => cat.id === selectedService.categoryId);
        if (category) {
          setSelectedCategory(category);
        }
      }
    } catch (err) {
      setError('Failed to load service categories');
      console.error('Error loading categories:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedService?.categoryId]);

  const loadServiceLevels = useCallback(async (categoryId) => {
    try {
      setLoadingLevels(true);
      setError(null);
      
      // First try to find the category in already loaded categories
      const category = categories.find(cat => cat.id === categoryId);
      if (category && category.serviceLevels) {
        setServiceLevels(category.serviceLevels);
        
        // Set default selection if provided
        if (selectedService?.levelId) {
          const level = category.serviceLevels.find(lev => lev.id === selectedService.levelId);
          if (level) {
            setSelectedLevel(level);
          }
        }
        return;
      }
      
      // Fallback to API call if not found in loaded categories
      const response = await serviceAPI.getServiceLevels(categoryId);
      setServiceLevels(response.data.serviceLevels);
      
      // Set default selection if provided
      if (selectedService?.levelId) {
        const level = response.data.serviceLevels.find(lev => lev.id === selectedService.levelId);
        if (level) {
          setSelectedLevel(level);
        }
      }
    } catch (err) {
      setError('Failed to load service levels');
      console.error('Error loading service levels:', err);
    } finally {
      setLoadingLevels(false);
    }
  }, [categories, selectedService?.levelId]);

  const calculatePricing = useCallback(async () => {
    if (!selectedCategory || !selectedLevel) return;
    
    // Create cache key for this pricing calculation
    const cacheKey = `${selectedCategory.id}-${selectedLevel.id}-${JSON.stringify(additionalServices)}`;
    
    // Check if we already have this pricing in cache
    if (pricingCache.current.has(cacheKey)) {
      const cachedPricing = pricingCache.current.get(cacheKey);
      setPricing(cachedPricing);
      
      // Notify parent component with cached data
      if (onServiceSelectRef.current) {
        onServiceSelectRef.current({
          category: selectedCategory,
          level: selectedLevel,
          pricing: cachedPricing,
          additionalServices
        });
      }
      return;
    }
    
    try {
      // Try dynamic pricing first, fallback to static
      let response;
      try {
        response = await serviceAPI.calculateDynamicPricing(
          selectedCategory.id,
          selectedLevel.id,
          additionalServices
        );
      } catch (error) {
        console.log('Dynamic pricing unavailable, using static calculation');
        response = await serviceAPI.calculatePricing(
          selectedCategory.id,
          selectedLevel.id,
          additionalServices
        );
      }
      
      const pricingData = response.data.pricing;
      setPricing(pricingData);
      
      // Cache the pricing result
      pricingCache.current.set(cacheKey, pricingData);
      
      // Notify parent component
      if (onServiceSelectRef.current) {
        onServiceSelectRef.current({
          category: selectedCategory,
          level: selectedLevel,
          pricing: pricingData,
          additionalServices
        });
      }
    } catch (err) {
      console.error('Error calculating pricing:', err);
    }
  }, [selectedCategory, selectedLevel, additionalServices]);

  useEffect(() => {
    loadCategories();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedCategory) {
      loadServiceLevels(selectedCategory.id);
    }
  }, [selectedCategory, loadServiceLevels]);

  useEffect(() => {
    if (selectedCategory && selectedLevel) {
      calculatePricing();
    }
  }, [selectedCategory, selectedLevel, additionalServices, calculatePricing]);

  // Cleanup cache on unmount
  useEffect(() => {
    const cache = pricingCache.current;
    return () => {
      cache.clear();
    };
  }, []);

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setSelectedLevel(null);
    setPricing(null);
    setServiceLevels([]); // Clear service levels
    setAdditionalServices({ bags: 1 }); // Reset additional services
    setError(null); // Clear any errors
  };

  const handleLevelChange = (level) => {
    setSelectedLevel(level);
    setPricing(null); // Clear pricing when changing level
    setAdditionalServices({ bags: 1 }); // Reset additional services
  };

  const handleAdditionalServicesChange = (service, value) => {
    setAdditionalServices(prev => ({
      ...prev,
      [service]: parseInt(value) || 0
    }));
  };

  const formatPrice = (price, currency = 'GBP') => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency
    }).format(price);
  };

  if (loading) {
    return <Loader isDarkMode={isDarkMode} />;
  }

  if (error) {
    return (
      <div className={`border rounded-lg p-4 transition-colors duration-300 ${
        isDarkMode ? 'bg-red-900 border-red-700' : 'bg-red-50 border-red-200'
      }`}>
        <p className={`transition-colors duration-300 ${
          isDarkMode ? 'text-red-200' : 'text-red-800'
        }`}>{error}</p>
        <button 
          onClick={loadCategories}
          className={`mt-2 px-4 py-2 rounded transition-colors duration-300 ${
            isDarkMode 
              ? 'bg-red-700 text-white hover:bg-red-600' 
              : 'bg-red-600 text-white hover:bg-red-700'
          }`}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Service Categories */}
      <div>
        <h3 className={`text-lg font-semibold mb-4 transition-colors duration-300 ${
          isDarkMode ? 'text-white' : 'text-gray-900'
        }`}>Choose Service Type</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {categories.map((category) => (
            <div
              key={category.id}
              onClick={() => handleCategoryChange(category)}
              className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                selectedCategory?.id === category.id
                  ? isDarkMode 
                    ? 'border-blue-400 bg-blue-900' 
                    : 'border-blue-500 bg-blue-50'
                  : isDarkMode
                    ? 'border-skyone-border-dark hover:border-skyone-inset-dark bg-skyone-card-dark'
                    : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <h4 className={`font-medium transition-colors duration-300 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>{category.name}</h4>
              <p className={`text-sm mt-1 transition-colors duration-300 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}>{category.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Service Levels */}
      {selectedCategory && (
        <div>
          <h3 className={`text-lg font-semibold mb-4 transition-colors duration-300 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>
            Choose {selectedCategory.name} Service Level
          </h3>
          {loadingLevels ? (
            <div className="flex justify-center py-8">
              <Loader isDarkMode={isDarkMode} />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {serviceLevels.map((level) => (
                <div
                  key={level.id}
                  onClick={() => handleLevelChange(level)}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    selectedLevel?.id === level.id
                      ? isDarkMode 
                        ? 'border-blue-400 bg-blue-900' 
                        : 'border-blue-500 bg-blue-50'
                      : isDarkMode
                        ? 'border-skyone-border-dark hover:border-skyone-inset-dark bg-skyone-card-dark'
                        : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h4 className={`font-medium transition-colors duration-300 ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>{level.name}</h4>
                    <span className={`text-sm font-semibold transition-colors duration-300 ${
                      isDarkMode ? 'text-blue-400' : 'text-blue-600'
                    }`}>
                      {formatPrice(level.basePrice, level.currency)}
                    </span>
                  </div>
                  <p className={`text-sm transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>{level.description}</p>
                  <div className="mt-2">
                    <p className={`text-xs transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      {level.features.length} features included
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Additional Services */}
      {selectedLevel && (
        <div>
          <h3 className={`text-lg font-semibold mb-4 transition-colors duration-300 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>Additional Services</h3>
          <div className={`p-4 rounded-lg transition-colors duration-300 ${
            isDarkMode ? 'bg-skyone-inset-dark' : 'bg-gray-50'
          }`}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Number of Bags
                </label>
                <select
                  value={additionalServices.bags}
                  onChange={(e) => handleAdditionalServicesChange('bags', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 transition-colors duration-300 ${
                    isDarkMode
                      ? 'bg-skyone-inset-dark text-white border-skyone-border-dark focus:ring-primary-500'
                      : 'bg-white text-gray-900 border-gray-300 focus:ring-blue-500'
                  }`}
                >
                  <option value={1}>1-4 bags (Included)</option>
                  <option value={5}>5-8 bags (+£50)</option>
                  <option value={9}>9-12 bags (+£100)</option>
                  <option value={13}>13-16 bags (+£150)</option>
                  <option value={17}>17-23 bags (+£200)</option>
                  <option value={24}>24-30 bags (+£250)</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pricing Summary */}
      {pricing && (
        <div className={`border rounded-lg p-4 transition-colors duration-300 ${
          isDarkMode ? 'bg-blue-900 border-blue-700' : 'bg-blue-50 border-blue-200'
        }`}>
          <h3 className={`text-lg font-semibold mb-3 transition-colors duration-300 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>Pricing Summary</h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className={`transition-colors duration-300 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}>Base Price:</span>
              <span className={`font-medium transition-colors duration-300 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>{formatPrice(pricing.basePrice, pricing.currency)}</span>
            </div>
            {pricing.additionalFees > 0 && (
              <div className="flex justify-between">
                <span className={`transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>Additional Fees:</span>
                <span className={`font-medium transition-colors duration-300 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>{formatPrice(pricing.additionalFees, pricing.currency)}</span>
              </div>
            )}
            <div className={`border-t pt-2 transition-colors duration-300 ${
              isDarkMode ? 'border-skyone-border-dark' : 'border-gray-200'
            }`}>
              <div className="flex justify-between">
                <span className={`text-lg font-semibold transition-colors duration-300 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>Total Price:</span>
                <span className={`text-lg font-bold transition-colors duration-300 ${
                  isDarkMode ? 'text-blue-400' : 'text-blue-600'
                }`}>
                  {formatPrice(pricing.totalPrice, pricing.currency)}
                </span>
              </div>
            </div>
            {pricing.pricingSource && (
              <div className={`mt-2 text-xs transition-colors duration-300 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>
                <span className={`inline-flex items-center px-2 py-1 rounded-full transition-colors duration-300 ${
                  isDarkMode ? 'bg-skyone-inset-dark text-zinc-300' : 'bg-gray-100 text-gray-700'
                }`}>
                  {pricing.pricingSource === 'website' ? '🌐 Live pricing' : '📋 Static pricing'}
                </span>
                {pricing.lastUpdated && (
                  <span className="ml-2">
                    Updated: {new Date(pricing.lastUpdated).toLocaleString()}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Service Features */}
      {selectedLevel && (
        <div>
          <h3 className={`text-lg font-semibold mb-4 transition-colors duration-300 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>Service Features</h3>
          <div className={`p-4 rounded-lg transition-colors duration-300 ${
            isDarkMode ? 'bg-skyone-inset-dark' : 'bg-gray-50'
          }`}>
            <ul className="space-y-2">
              {selectedLevel.features.map((feature, index) => (
                <li key={index} className="flex items-start">
                  <span className={`mr-2 transition-colors duration-300 ${
                    isDarkMode ? 'text-green-400' : 'text-green-500'
                  }`}>✓</span>
                  <span className={`transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceSelector;
